import { addFirm, updateFirm } from '../store/slices/firmsSlice';
import { addPerson } from '../store/slices/personsSlice';
import { addFirmContact, addVisibleFirm, addUnaffiliatedContact } from '../store/slices/firmInfoSlice';
import { addVisibleFirmContact, addVisibleFirmWithContacts } from '../store/slices/orgContactsSlice';
import { setReferringContact, addEmergencyContact } from '../store/slices/contactInfoSlice';

import { createStdRequest, apiFetch, validatePhoneNumber, validateEmail, validateAddress } from '../utils/utils';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';

class FormProcessor {
  store = undefined;
  state = {};

  setStore(aStore) {
    this.store = aStore;
  }

  validateForm(state, validationPolicy) {
    this.state = state;
    let validation = { valid: true };

    if (validationPolicy.requireFirmName) {
      validation = this.validateCompanyName();
      if (!validation.valid) {
        return validation;
      }
    }

    if (validationPolicy.requireFirmType) {
      validation = this.validateFirmType();
      if (!validation.valid) {
        return validation;
      }
    }

    validation = this.validateAddress(validationPolicy);
    if (!validation.valid) {
      return validation;
    }

    if (validationPolicy.requireContact) {
      validation = this.validateContact(validationPolicy);
      if (!validation.valid) {
        return validation;
      }
    }

    if (validationPolicy.requireAtLeaseOneOfCompanyPhoneOrEmail) {
      validation = this.validateContactPhoneOrEmail();
      if (!validation.valid) {
        return validation;
      }
    }

    return { valid: true };
  }

  validateFirmType() {
    if (this.state.firmType) {
      return { valid: true };
    } else {
      return { valid: false, errorMessage: 'Serious Problem: no firmType specified' };
    }
  }

  validateCompanyName() {
    if (this.state.companyName.trim().length === 0) {
      return { valid: false, errorMessage: 'You must supply a Company Name' };
    } else {
      return { valid: true };
    }
  }

  validateAddress(validationPolicy) {
    let { addressLine1, addressLine2, city, state, zip } = this.state;
    if (state === 'select') {
      state = '';
    }
    const { requireAddress, forbidPartialAddress, allowZipFreeAddress } = validationPolicy;
    return validateAddress(
      addressLine1,
      addressLine2,
      city,
      state,
      zip,
      requireAddress,
      forbidPartialAddress,
      allowZipFreeAddress
    );
  }

  validateContact(validationPolicy) {
    let validation = this.validateContactName();
    if (!validation.valid) {
      return validation;
    }

    if (validationPolicy.requireJobTitle) {
      if (!this.state.contactJobTitle.trim()) {
        return {
          valid: false,
          errorMessage: 'You must supply a Job Title for the Contact',
        };
      }
    }

    if (validationPolicy.requireRelationship) {
      if (!this.state.relationship || !this.state.relationship.trim()) {
        return {
          valid: false,
          errorMessage: 'You must supply a Relationship for the Contact',
        };
      }
    }

    if (validationPolicy.requireContactDescription) {
      if (!this.state.contactDescription.trim()) {
        return {
          valid: false,
          errorMessage: 'You must supply a Description for the Contact',
        };
      }
    }

    if (validationPolicy.requireContactPhone) {
      validation = this.validateContactPhones();
      if (!validation.valid) {
        return validation;
      }
    }

    if (validationPolicy.requireContactEmail) {
      validation = this.validateContactEmail();
      if (!validation.valid) {
        return validation;
      }
    }

    if (validationPolicy.requireAtLeaseOneOfContactPhoneOrEmail) {
      validation = this.validateContactPhoneOrEmail();
      if (!validation.valid) {
        return validation;
      }
    }

    validation = this.validateContactPhonesAndRolesConsistency();
    if (!validation.valid) {
      return validation;
    }

    return { valid: true };
  }

  validateContactName() {
    const rawName = this.state.contactName;
    const name = rawName ? rawName.trim() : '';
    const valid = name.length >= 6 && name.indexOf(' ') > 1;
    const errorMessage = valid ? '' : 'You must supply a reasonable Contact Name';
    return { valid, errorMessage };
  }

  validateContactPhones() {
    // If we get here, there must be at least one phone number, so
    //  If there isn't one, report that problem,
    //  If there is, then make sure, for both phone numbers, that there's a role
    //    if and only if there's a number, and the number, if it exists, is valid.
    if (!this.state.contactPhone1Number) {
      return { valid: false, errorMessage: 'You must supply at least one phone number' };
    }

    let validation = this.validatePhoneNumberAndRoles(
      this.state.contactPhone1Number,
      this.state.contactPhone1Roles,
      'Phone 1'
    );
    if (!validation.valid) {
      return validation;
    }

    validation = this.validatePhoneNumberAndRoles(
      this.state.contactPhone2Number,
      this.state.contactPhone2Roles,
      'Phone 2'
    );
    if (!validation.valid) {
      return validation;
    }

    return { valid: true };
  }

  validateContactEmail() {
    if (!this.state.contactEmail) {
      return { valid: false, errorMessage: 'You must supply an email address for the contact' };
    }
    return validateEmail(this.state.contactEmail, 'Email does not appear to be in a valid format');
  }

  validatePhoneNumberAndRoles(number, roles, label) {
    if (number) {
      const validation = validatePhoneNumber(number, label);
      if (!validation.valid) {
        return validation;
      } else {
        return this.validateContactPhoneAndRolesConsistency(number, roles, label);
      }
    } else {
      return this.validateContactPhoneAndRolesConsistency(number, roles, label);
    }
  }

  validateContactPhoneOrEmail() {
    const { contactEmail, contactPhone1Number, contactPhone2Number } = this.state;
    if (contactEmail || contactPhone1Number) {
      if (contactEmail) {
        const emailValidation = validateEmail(contactEmail, "The supplied Contact Email doesn't appear to be valid");
        if (!emailValidation.valid) {
          return emailValidation;
        }
        // We have a good email, but we still want to check on the validity of Phone1
        if (contactPhone1Number) {
          const phoneValidation = validatePhoneNumber(contactPhone1Number, 'Phone 1');
          if (!phoneValidation.valid) {
            return phoneValidation;
          }
        } else {
          return emailValidation;
        }
        // We have a good email and Phone1, but we still want to check on the validity of Phone2, if any
        if (contactPhone2Number) {
          return validatePhoneNumber(contactPhone2Number, 'Phone 2');
        } else {
          return emailValidation;
        }
      } else {
        // We have no email, but at least one phone number
        const phone1Validation = validatePhoneNumber(contactPhone1Number, 'Phone 1');
        if (!phone1Validation.valid) {
          return phone1Validation;
        }
        // We have a good Phone1, but we still want to check on the validity of Phone2, if any
        if (contactPhone2Number) {
          return validatePhoneNumber(contactPhone2Number, 'Phone 2');
        }
        return phone1Validation;
      }
    } else {
      return {
        valid: false,
        errorMessage: 'You must supply (at least) either a phone number or an email address for the Contact',
      };
    }
  }

  validateContactPhonesAndRolesConsistency() {
    const { contactPhone1Number, contactPhone1Roles, contactPhone2Number, contactPhone2Roles } = this.state;
    const validation = this.validateContactPhoneAndRolesConsistency(contactPhone1Number, contactPhone1Roles, 'Phone 1');
    if (!validation.valid) {
      return validation;
    }
    return this.validateContactPhoneAndRolesConsistency(contactPhone2Number, contactPhone2Roles, 'Phone 2');
  }

  validateContactPhoneAndRolesConsistency(number, roles, label) {
    if (number) {
      if (roles) {
        return { valid: true };
      } else {
        return {
          valid: false,
          errorMessage: 'For ' + label + ', since you have supplied a number, you must also check a role',
        };
      }
    } else {
      if (roles) {
        return {
          valid: false,
          errorMessage: 'For ' + label + ', since you have  checked a role, you must also supply a number',
        };
      } else {
        return { valid: true };
      }
    }
  }

  submitOrgWithoutContactForm(form) {
    this.state = { ...this.state, ...form.state };

    const uri = '/api/firm';
    const firmApiStruct = this.buildContactFreeFirmApiStruct();
    const request = createStdRequest(uri, 'POST', firmApiStruct);
    const actionOnGoodResponse = (json) => {
      let createFirmResponse = json;

      const contactIds = [];
      const description = this.state.companyDescription.trim();
      const officePhone = this.state.contactPhone1Number || '';
      createFirmResponse = { ...createFirmResponse, contactIds, description, officePhone };
      this.store.dispatch(addFirm(createFirmResponse));

      // TODO How much of this is redundant?
      const viewingFirmId = DA.getFirmId();
      const uri = `/api/firm/${viewingFirmId}/visibleFirm`;
      const visibleFirmId = createFirmResponse.firmId;
      const firmType = this.state.firmType;
      const temperature = this.state.companyTemperature;
      const email = this.state.contactEmail;
      const f2fVisibilityApiStruct = { viewingFirmId, visibleFirmId, firmType, description, temperature, email };
      const innerRequest = createStdRequest(uri, 'post', f2fVisibilityApiStruct);
      const innerActionOnGoodResponse = () => {
        const firmType = createFirmResponse.firmType;
        const visibleFirmName = createFirmResponse.name;
        const simpleContacts = [];
        const firmStruct = {
          firmType,
          firmId: visibleFirmId,
          name: visibleFirmName,
          contacts: simpleContacts,
        };
        this.store.dispatch(addVisibleFirm(firmStruct));

        this.store.dispatch(addVisibleFirmWithContacts({ visibleFirmId, contactIdsArray: contactIds }));

        form.erase();
        form.onSuccessfulSubmission(firmType, visibleFirmId, 0);
      };
      apiFetch(form, innerRequest, innerActionOnGoodResponse);
    };
    apiFetch(form, request, actionOnGoodResponse);
  }

  submitOrgAndContactForm(form) {
    this.state = { ...this.state, ...form.state };

    const uri = '/api/firm';
    const firmApiStruct = this.buildFirmApiStruct();
    const request = createStdRequest(uri, 'POST', firmApiStruct);
    const actionOnGoodResponse = (json) => {
      let createFirmResponse = json;

      const { contacts } = createFirmResponse;
      const contact = contacts[0];
      const contactId = contact.personId;
      const contactIds = [contactId];
      const description = this.state.companyDescription.trim();
      createFirmResponse = { ...createFirmResponse, contactIds, description };
      this.store.dispatch(addFirm(createFirmResponse));

      const reduxContactStruct = ReduxLoader.createReduxStructForFetchedPersonOrContactApiStruct(contact);
      this.store.dispatch(addPerson(reduxContactStruct));

      // TODO How much of this is redundant?
      const viewingFirmId = DA.getFirmId();
      const uri = `/api/firm/${viewingFirmId}/visibleFirm`;
      const visibleFirmId = createFirmResponse.firmId;
      const firmType = this.state.firmType;
      const temperature = this.state.companyTemperature;
      const f2fVisibilityApiStruct = { viewingFirmId, visibleFirmId, firmType, description, temperature };
      const innerRequest = createStdRequest(uri, 'post', f2fVisibilityApiStruct);
      const innerActionOnGoodResponse = () => {
        const firmType = createFirmResponse.firmType;
        const visibleFirmName = createFirmResponse.name;
        const simpleContact = { id: contact.personId, name: reduxContactStruct.preferredFullName };
        const simpleContacts = [simpleContact];
        const firmStruct = {
          firmType,
          firmId: visibleFirmId,
          name: visibleFirmName,
          contacts: simpleContacts,
        };
        this.store.dispatch(addVisibleFirm(firmStruct));

        this.store.dispatch(addVisibleFirmWithContacts({ visibleFirmId, contactIdsArray: contactIds }));

        form.erase();
        form.onSuccessfulSubmission(firmType, visibleFirmId, contactId);
      };
      apiFetch(form, innerRequest, innerActionOnGoodResponse);
    };
    apiFetch(form, request, actionOnGoodResponse);
  }

  buildFirmApiStruct() {
    /* expected structure:
      public int FirmId { get; set; }
      public string Name { get; set; }
      public string FirmType { get; set; }
      public string Description { get; set; }
      public string SalesLine { get; set; }
      public string Temperature { get; set; }
      public List<FirmContactApiStruct> Contacts { get; set; } = new List<FirmContactApiStruct>();
      public List<FacilityApiStruct> Facilities { get; set; } = new List<FacilityApiStruct>();
      public List<int> ReferralFromIds { get; set; } = new List<int>();
      public List<ReferralApiStruct> ReferralsTo { get; set; } = new List<ReferralApiStruct>();
      public List<WebLinkApiStruct> WebLinks { get; set; }
  */
    const { companyName, webUrl, firmType, companyDescription, companyTemperature } = this.state;
    const contactApiStruct = this.buildContactApiStructForNewFirm();
    const facilityApiStruct = this.buildFacilityApiStruct();
    const webLinks = webUrl ? [{ linkType: 'Website', url: webUrl }] : [];
    return {
      firmId: 0,
      name: companyName.trim(),
      firmType,
      description: companyDescription || '',
      temperature: companyTemperature,
      contacts: [contactApiStruct],
      facilities: [facilityApiStruct],
      webLinks,
    };
  }

  buildContactFreeFirmApiStruct() {
    /* expected structure:
      public int FirmId { get; set; }
      public string Name { get; set; }
      public string FirmType { get; set; }
      public string Description { get; set; }
      public string SalesLine { get; set; }
      public string Temperature { get; set; }
      public List<FirmContactApiStruct> Contacts { get; set; } = new List<FirmContactApiStruct>();
      public List<FacilityApiStruct> Facilities { get; set; } = new List<FacilityApiStruct>();
      public List<int> ReferralFromIds { get; set; } = new List<int>();
      public List<ReferralApiStruct> ReferralsTo { get; set; } = new List<ReferralApiStruct>();
      public List<WebLinkApiStruct> WebLinks { get; set; }
  */
    const { companyName, webUrl, firmType, companyDescription, contactPhone1Number, companyTemperature } = this.state;
    const facilityApiStruct = this.buildFacilityApiStruct();
    const webLinks = webUrl ? [{ linkType: 'Website', url: webUrl }] : [];
    return {
      firmId: 0,
      name: companyName.trim(),
      firmType,
      description: companyDescription || '',
      salesLine: contactPhone1Number,
      temperature: companyTemperature,
      contacts: [],
      facilities: [facilityApiStruct],
      webLinks,
    };
  }

  buildContactApiStructForNewFirm() {
    return this.buildContactApiStructForViewingAndVisibleFirms(0, 0);
  }

  buildAddressApiStruct() {
    /*
    public string Roles { get; set; }
    public string IsIntlStr { get; set; }
    public string Line1 { get; set; }
    public string Line2 { get; set; }
    public string City { get; set; }
    public string State { get; set; }
    public string Zip { get; set; }
    public string IntlAddr { get; set; }
  */
    const { addressLine1, addressLine2, city, state, zip } = this.state;
    if (addressLine1 && addressLine1.trim()) {
      return {
        roles: 'Business',
        isIntlStr: 'false',
        line1: addressLine1.trim(),
        line2: addressLine2 ? addressLine2.trim() : '',
        city: city.trim(),
        state,
        zip: zip.trim(),
      };
    } else {
      return null;
    }
  }

  buildFacilityApiStruct() {
    /*
    public int FacilityId { get; set; }
    public string Name { get; set; }
    public int FirmId { get; set; }
    public string PictureUrl { get; set; }
    public AddressApiStruct FacilityAddress { get; set; }
    public List<PhoneApiStruct> PhoneNumbers { get; set; } = new List<PhoneApiStruct>();
  */
    const phoneNumbers = [];
    const addressApiStruct = this.buildAddressApiStruct();
    return {
      facilityId: 0,
      name: this.state.companyName,
      firmId: 0,
      facilityAddress: addressApiStruct,
      phoneNumbers: phoneNumbers,
    };
  }

  submitOrgContactForm(form) {
    this.state = { ...this.state, ...form.state };
    this.checkForPossibleDuplicateAndAcceptFirmContact(form);
  }

  submitUnafilliatedContactForm(form) {
    this.state = { ...this.state, ...form.state };
    this.checkForPossibleDuplicateAndAcceptUnaffiliatedContact(form);
  }

  checkForPossibleDuplicateAndAcceptFirmContact(form) {
    const viewingFirmId = DA.getFirmId();
    const visibleFirmId = form.props.firmId;
    const firmContactApiStruct = this.buildContactApiStructForViewingAndVisibleFirms(viewingFirmId, visibleFirmId);

    // Check for possible duplicate
    const looksLikeDuplicate = this.checkForDuplicate(firmContactApiStruct);
    if (looksLikeDuplicate) {
      form.showConfirmDuplicateUI(firmContactApiStruct);
    } else {
      this.acceptFirmContact(form, firmContactApiStruct);
    }
  }

  checkForPossibleDuplicateAndAcceptUnaffiliatedContact(form) {
    const viewingFirmId = DA.getFirmId();
    const visibleFirmId = 0;
    const firmContactApiStruct = this.buildContactApiStructForViewingAndVisibleFirms(viewingFirmId, visibleFirmId);

    // Check for possible duplicate
    const looksLikeDuplicate = this.checkForDuplicate(firmContactApiStruct);
    if (looksLikeDuplicate) {
      form.showConfirmDuplicateUI(firmContactApiStruct);
    } else {
      this.acceptUnaffiliatedContact(form, firmContactApiStruct);
    }
  }

  checkForDuplicate(firmContactApiStruct) {
    const { firmId: visibleFirmId, firstName, lastName, email, phoneNumbers } = firmContactApiStruct;

    const existingContacts = DA.getContactsAtVisibleFirm(visibleFirmId || 0);
    if (existingContacts.length === 0) {
      return false;
    }

    const existingContactsWithSameName = existingContacts.filter(
      (c) => c.firstName === firstName && c.lastName === lastName
    );
    if (existingContactsWithSameName.length === 0) {
      return false;
    }

    if (email) {
      return existingContactsWithSameName.some((c) => c.email === email);
    }

    phoneNumbers.forEach((phoneNumber) => {
      if (phoneNumber) {
        return existingContactsWithSameName.some((c) => this.contactHasPhoneNumber(c, phoneNumber));
      }
    });

    return true; // same name, but no phone number or email
  }

  acceptFirmContact(form, firmContactApiStruct) {
    const uri = `/api/firm/${firmContactApiStruct.viewingFirmId}/visibleFirm/${firmContactApiStruct.referralFirmId}/contact`;
    const request = createStdRequest(uri, 'post', firmContactApiStruct);
    const actionOnGoodResponse = (responseStruct) => {
      const { personId, referralFirmId } = responseStruct;
      const reduxContactStruct = ReduxLoader.createReduxStructForFetchedPersonOrContactApiStruct(responseStruct);
      const personIdAndName = { id: personId, name: reduxContactStruct.preferredFullName };
      this.store.dispatch(addFirmContact({ referralFirmId, personIdAndName }));
      this.store.dispatch(addPerson(reduxContactStruct));
      this.store.dispatch(addVisibleFirmContact({ visibleFirmId: referralFirmId, contactId: personId }));
      const visibleFirm = DA.getFirmById(referralFirmId);
      const extendedContactIds = visibleFirm.contactIds.concat(personId);
      const firmWithExtraContact = { ...visibleFirm, contactIds: extendedContactIds };
      this.store.dispatch(updateFirm(firmWithExtraContact));

      form.onSuccessfulSubmission(personId);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  acceptUnaffiliatedContact(form, firmContactApiStruct) {
    const uri = `/api/firm/${firmContactApiStruct.viewingFirmId}/visibleFirm/0/contact`;
    const request = createStdRequest(uri, 'post', firmContactApiStruct);
    const actionOnGoodResponse = (responseStruct) => {
      const { personId } = responseStruct;
      const reduxContactStruct = ReduxLoader.createReduxStructForFetchedPersonOrContactApiStruct(responseStruct);
      const personIdAndName = { id: personId, name: reduxContactStruct.preferredFullName };
      this.store.dispatch(addUnaffiliatedContact(personIdAndName));
      this.store.dispatch(addPerson(reduxContactStruct));

      form.onSuccessfulSubmission(personId);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  buildContactApiStructForViewingAndVisibleFirms(viewingFirmId, visibleFirmId) {
    /*
    public int PersonId { get; set; }
    public string Email { get; set; }
    public string FirstName { get; set; }
    public string MiddleNames { get; set; }
    public string LastName { get; set; }
    public string PreferredName { get; set; }
    public string Sex { get; set; }
    public long BirthDate { get; set; }
    public long LatestContactDate { get; set; }
    public OfficialIdApiStruct OfficialId { get; set; }
    public List<PhoneApiStruct> PhoneNumbers { get; set; } = new List<PhoneApiStruct>();
    public List<AddressApiStruct> Addresses { get; set; } = new List<AddressApiStruct>();
    
    public int FirmId { get; set; }
    public int ViewingFirmId { get; set; }
    public string JobTitle { get; set; }
    public string Description { get; set; }
    public string Temperature { get; set; }
  */
    const { contactName, contactJobTitle, contactDescription, contactEmail, contactOwnerId, contactTemperature } =
      this.state;

    const trimmedName = contactName.trim();
    const firstSpaceLoc = trimmedName.indexOf(' ');
    const lastSpaceLoc = trimmedName.lastIndexOf(' ');
    const firstName = trimmedName.substring(0, firstSpaceLoc);
    const lastName = lastSpaceLoc >= firstSpaceLoc ? trimmedName.substring(lastSpaceLoc + 1) : '';
    const middleNames =
      lastSpaceLoc > firstSpaceLoc ? trimmedName.substring(firstSpaceLoc + 1, lastSpaceLoc).trim() : '';
    const phoneNumbers = this.buildContactPhoneNumbersList();
    let addresses = [];
    const address = this.buildAddressApiStruct();
    if (address) {
      addresses = [address];
    }
    return {
      personId: 0,
      email: contactEmail,
      firstName: firstName,
      middleNames: middleNames,
      lastName: lastName,
      preferredName: firstName,
      sex: 'Unknown',
      birthDate: null,
      latestContactDate: '',
      phoneNumbers,
      addresses,
      viewingFirmId: viewingFirmId,
      referralFirmId: visibleFirmId,
      jobTitle: contactJobTitle.trim(),
      description: contactDescription.trim(),
      inactive: false,
      ownerId: contactOwnerId,
      temperature: contactTemperature,
    };
  }

  buildContactPhoneNumbersList() {
    const { contactPhone1Roles, contactPhone1Number, contactPhone2Roles, contactPhone2Number } = this.state;

    let phoneNumbers = [];
    if (contactPhone1Number) {
      const phoneApiStruct = { roles: contactPhone1Roles, countryCode: 1, number: contactPhone1Number };
      phoneNumbers = [phoneApiStruct];
    }
    if (contactPhone2Number) {
      const phoneApiStruct = { roles: contactPhone2Roles, countryCode: 1, number: contactPhone2Number };
      phoneNumbers = [...phoneNumbers, phoneApiStruct];
    }

    return phoneNumbers;
  }

  submitPersonalReferrerForm(form) {
    this.state = { ...this.state, ...form.state };

    const suppliedRelationship = this.state.relationship;
    const validRelationship = (suppliedRelationship && suppliedRelationship.trim()) || 'Unknown';
    this.state.relationship = validRelationship;

    this.acceptRelatedContact(form);
  }

  acceptRelatedContact(form) {
    // TODO should really make this a choice available in the UI
    // const { beEmergencyContact } = this.state;
    const beEmergencyContact = true;

    const uri = '/api/person';
    const contactApiStruct = this.buildContactApiStructForPersonalReferrer();
    const request = createStdRequest(uri, 'post', contactApiStruct);
    const actionOnGoodResponse = (json) => {
      const contact = json;

      const reduxContactStruct = ReduxLoader.createReduxStructForFetchedPersonOrContactApiStruct(contact);
      this.store.dispatch(addPerson(reduxContactStruct));

      const relationship = this.state.relationship;
      this.store.dispatch(setReferringContact(contact, relationship));
      if (beEmergencyContact) {
        const { personId, email, phoneNumbers } = contact;
        const name = reduxContactStruct.preferredFullName;
        const contactStruct = {
          personId,
          relationship,
          name,
          email,
          phoneNumbers,
        };
        this.store.dispatch(addEmergencyContact(contactStruct));

        form.onSuccessfulSubmission(personId);
      }
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  buildContactApiStructForPersonalReferrer() {
    const { contactName, contactEmail } = this.state;
    const personId = 0;
    const email = contactEmail;
    const trimmedContactName = contactName.trim();
    const firstSpaceLoc = trimmedContactName.indexOf(' ');
    const lastSpaceLoc = trimmedContactName.lastIndexOf(' ');
    const firstName = trimmedContactName.substring(0, firstSpaceLoc);
    const middleNames = trimmedContactName.slice(firstSpaceLoc, lastSpaceLoc).trim();
    const lastName = trimmedContactName.substring(lastSpaceLoc + 1);
    const preferredName = firstName;
    const phoneNumbers = this.buildContactPhoneNumbersList();
    const addresses = [];
    return {
      personId,
      email,
      firstName,
      middleNames,
      lastName,
      preferredName,
      phoneNumbers,
      addresses,
    };
  }
}

export default new FormProcessor();
